import { useEffect, useState } from "react";
import { useNavigate } from "@usas/ui-web/wrappers/ReactRouterDom";

import useClubStaffData from "../../../state/clubStaff/UseClubStaffData";
import useClubBaseInfoData from "../../../state/clubBaseInfo/UseClubBaseInfoData";

import useNavRoutes from "@usas/ui-web/state/security/UseNavRoutes";
import useBaseUsasClubData from "@usas/ui-web/state/clubBase/UseBaseUsasClubData";
import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';

import { DEFAULT_ID } from "@usas/ui-web/utils/Constants";

const INITIAL_STATE = {
  pageErrorMessage: ''
};

const INITIAL_DELETE_MODAL_STATE = {
  personOrgRoleDurationId: DEFAULT_ID,
  personName: '',
  orgRoleName: '',
  isLastRole: false,
  isRegistrationRole: false,
  displayModal: false
};

const useClubStaff = () => {
  const navigate = useNavigate();
  const { navRoutes } = useNavRoutes();
  const { environmentVariableState, CLUB_STAFF_ROLE_GROUP_ID } = useEnvironmentVariableData();
  const { baseUsasClubState } = useBaseUsasClubData();
  const { clubBaseInfoState, getClubBaseInfo } = useClubBaseInfoData();
  const { clubStaffState, clubStaffDeleteState, getClubStaffMembers, deleteClubStaffRole } = useClubStaffData();
  const [state, setState] = useState(INITIAL_STATE);
  const [deleteModalState, setDeleteModalState] = useState(INITIAL_DELETE_MODAL_STATE);

  const onAddClicked = (e) => {
    try {
      e?.preventDefault();

      navigate(navRoutes.CLUB_STAFF_DETAIL?.route);
    } catch (error) {
      setState({
        ...state,
        pageErrorMessage: error.message
      });
    }
  };

  const onEditClicked = (e, clubStaff) => {
    try {
      e?.preventDefault();
      e?.stopPropagation();

      navigate(navRoutes.CLUB_STAFF_DETAIL?.route, { state: { clubStaff } });
    } catch (error) {
      setState({
        ...state,
        pageErrorMessage: error.message
      });
    }
  };

  const onDeleteClicked = (e, personId, personOrgRoleDurationId) => {
    try {
      e?.preventDefault();
      e?.stopPropagation();

      const targetPerson = clubStaffState.arrayData.find(x => x.personId === personId);

      if (targetPerson && Array.isArray(targetPerson.personOrgRole)) {
        const targetOrgRole = targetPerson.personOrgRole.find(x => x.personOrgRoleDurationId === personOrgRoleDurationId);
        const isLastRole = targetPerson.personOrgRole.length === 1 && targetPerson.personOrgRole[0].personOrgRoleDurationId === personOrgRoleDurationId;

        setDeleteModalState({
          ...deleteModalState,
          personOrgRoleDurationId,
          personName: `${targetPerson.firstName} ${targetPerson.lastName}`,
          orgRoleName: targetOrgRole.orgRoleName,
          isLastRole,
          isRegistrationRole: targetOrgRole.isPrimary === true,
          displayModal: true
        });
      }
    } catch (error) {
      setState({
        ...state,
        pageErrorMessage: error.message
      });
    }
  };

  const onDeleteModalConfirmClicked = async (e) => {
    try {
      e?.preventDefault();

      setDeleteModalState(INITIAL_DELETE_MODAL_STATE);

      await deleteClubStaffRole(deleteModalState.personOrgRoleDurationId);
      await getClubBaseInfo(baseUsasClubState.objData.usasClubId);
      await getClubStaffMembers(baseUsasClubState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID);
    } catch (error) {
      setState({
        ...state,
        pageErrorMessage: error.message
      });
    }
  };

  const onDeleteModalCancelClicked = (e) => {
    try {
      e?.preventDefault();

      setDeleteModalState(INITIAL_DELETE_MODAL_STATE);
    } catch (error) {
      setState({
        ...state,
        pageErrorMessage: error.message
      });
    }
  };

  useEffect(() => {
    async function loadClubStaffMembers() {
      try {
        if (baseUsasClubState.isObjLoaded === true && environmentVariableState.isLoaded === true) {
          if (clubStaffState.isArrayLoaded !== true) {
            await getClubStaffMembers(baseUsasClubState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID);
          }
        }
      } catch (error) {
        setState({
          ...state,
          pageErrorMessage: error.message
        });
      }
    }
    loadClubStaffMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps   
  }, [baseUsasClubState.isObjLoaded, environmentVariableState]);

  return {
    isLoading: environmentVariableState.isLoading || clubBaseInfoState.isObjLoading,
    isSaving: clubStaffDeleteState.isObjLoading,
    isGridLoading: clubStaffState.isArrayLoading,
    gridData: clubStaffState.arrayData,
    state,
    deleteModalState,
    onAddClicked,
    onEditClicked,
    onDeleteClicked,
    onDeleteModalConfirmClicked,
    onDeleteModalCancelClicked
  };
};

export default useClubStaff;